/**
 * Frontend TZ helpers — usadas por widget / customer portal / admin.
 *
 * Closeout de los hallazgos M-8 y M-9 de la auditoría profunda. Reemplazan
 * los patrones crudos `new Date().toISOString().slice(0, 10)` y `new Date()
 * + setDate(...)` que defaulteaban al UTC del navegador y producían
 * desalineaciones cuando el operador (admin) o el cliente final (widget)
 * estaban en una zona distinta a la del centro.
 *
 * NO usar `new Date().toISOString().slice(0, 10)` para defaults de filtros
 * de fecha — usar `todayInTimezone(tz)`.
 *
 * NO usar `new Date(); d.setDate(d.getDate() + i)` para generar días
 * consecutivos — usar `nextDaysInTimezone(n, tz)`.
 */

/**
 * Devuelve la fecha calendárica actual (YYYY-MM-DD) en la zona horaria
 * indicada. Independiente del reloj del navegador.
 */
export function todayInTimezone(timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * Devuelve los próximos `n` días calendáricos (YYYY-MM-DD) empezando por
 * hoy, todo en la zona del centro. Para n=14 desde widget/customer eso da
 * la franja de fechas seleccionables sin saltos ni duplicados ya seas
 * cliente en UTC-5 reservando en un centro de Madrid o al revés.
 */
export function nextDaysInTimezone(n: number, timezone: string): string[] {
  const todayIso = todayInTimezone(timezone);
  const [yearStr, monthStr, dayStr] = todayIso.split('-');
  // Anclamos la base en UTC para que `setUTCDate(+i)` avance días
  // calendáricos sin que la TZ del navegador interfiera.
  const baseUtc = new Date(Date.UTC(Number(yearStr), Number(monthStr) - 1, Number(dayStr)));
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  // OJO: formateamos cada día como mediodía UTC para evitar que la
  // conversión a la TZ del centro caiga en un edge case de DST que
  // mueva el día calendárico ±1 (los cambios DST ocurren en las
  // primeras horas; mediodía está siempre en el día "correcto").
  const days: string[] = [];
  for (let i = 0; i < n; i++) {
    const noon = new Date(baseUtc);
    noon.setUTCDate(baseUtc.getUTCDate() + i);
    noon.setUTCHours(12, 0, 0, 0);
    days.push(fmt.format(noon));
  }
  return days;
}
